import debug from "src/modules/debug-logger";
import Presence from "src/constants/presence";
import parseDevice from "./parseDevice";
import { client, jid } from "@xmpp/client";
import setupRoster from "@xmpp-plugins/roster";
import { getEnvironmentVariable } from "src/utils/environment";

const log = window.require("electron-log");
const xmppServer = getEnvironmentVariable("XMPP_SERVER");

export default function initializeXmppClient({ messenger }) {
  debug.messenger(
    `Initializing XMPP connection, to ${xmppServer}, jid: ${messenger.xmppLogin}`
  );
  const xmpp = client({
    service: xmppServer,
    username: messenger.xmppLogin,
    password: messenger.xmppPassword,
    domain: getEnvironmentVariable("XMPP_DOMAIN"),
    resource: getEnvironmentVariable("XMPP_RESOURCE")
  });
  // we handle reconnect manually
  xmpp.reconnect.stop();

  const originalSend = xmpp.send.bind(xmpp);
  xmpp.send = async (...args) => {
    try {
      await originalSend(...args);
      return true;
    } catch (error) {
      log.error("xmpp.send failed");
      return false;
    }
  };

  const originalStop = xmpp.stop.bind(xmpp);
  xmpp.stop = async (...args) => {
    debug.messenger(`xmpp.stop called, xmpp.status === ${xmpp.status}`);
    if (xmpp.status === "close" || xmpp.status === "closing") return;
    try {
      xmpp.removeAllListeners();
      await originalStop();
    } catch (error) {
      debug.messenger("xmpp.stop() failed");
    }
  };

  const roster = setupRoster(xmpp);
  xmpp.getRoster = async function getRoster() {
    const { items } = await roster.get();
    const parsedDevices = items.map(parseDevice).map((device) => {
      device.status = Presence.UNAVAILABLE;
      return device;
    });

    return parsedDevices;
  };

  roster.on("set", ({ item, version }) => {
    debug.xmpp("roster.on('set')", item);
    const parsedDevice = parseDevice(item);
    messenger.emit("roster-set", parsedDevice);
  });

  roster.on("remove", ({ jid, verison }) => {
    debug.xmpp("roster.on('remove')", jid);
    messenger.emit("roster-remove", jid.local);
  });

  xmpp.on("error", (error) => {
    log.error(error);
    messenger.emit("xmpp-error", error);
  });
  xmpp.on("online", (xmppJID) => {
    debug.xmpp("Xmpp online event fired", xmppJID);
  });
  xmpp.on("offline", () => {
    debug.xmpp("offline");
  });
  xmpp.reconnect.on("reconnecting", () => {
    debug.xmpp("reconnecting");
  });

  xmpp.on("stanza", (stanza) => {
    const { from } = stanza.attrs;

    switch (stanza.name) {
      case "presence": {
        const xmppLogin = jid(from).local;
        if (xmppLogin === messenger.xmppLogin) {
          return;
        }
        messenger.processPresenceStanza(stanza);
        return;
      }

      case "message":
        messenger.processMessageStanza(stanza);
        return;

      case "iq": {
        const vCard = stanza.getChild("vCard");
        const error = stanza.getChild("error");
        if (vCard) {
          const xmppLogin = jid(from).local;
          messenger.processVcard({ vCard, xmppLogin, error });
        }
        return;
      }

      default:
        debug.xmpp("Received unhandled stanza:", stanza);
        return;
    }
  });

  xmpp.on("status", (status) => {
    debug.messenger("xmpp status:", status);
    messenger.emit("xmpp-status", status);
  });

  xmpp.on("input", (input) => {
    debug.xmpp("Received:", input);
  });

  xmpp.on("output", (output) => {
    debug.xmpp("Sent:", output);
  });

  return xmpp;
}
